import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompanyReviews } from '@/hooks/queries/useCompanyReviews';
import type { CompanyReviewDto } from '@/types/dto/company-review.dto';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ExternalLink, MessageSquareText, RefreshCw, Star } from 'lucide-react';

interface CompanyReviewsProps {
  companyId: string;
  enabled: boolean;
  fallbackRating?: number | null;
  fallbackTotal?: number;
}

function RatingStars({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'lg' }) {
  const iconClassName = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

  return (
    <div className="flex gap-0.5" aria-label={`${rating} de 5 estrelas`}>
      {[1, 2, 3, 4, 5].map((value) => (
        <Star
          key={value}
          aria-hidden="true"
          className={`${iconClassName} ${value <= rating ? 'fill-warning text-warning' : 'fill-muted text-muted'}`}
        />
      ))}
    </div>
  );
}

function getInitials(name: string | null) {
  if (!name) return 'A';

  return name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function ReviewCard({ review }: { review: CompanyReviewDto }) {
  const authorName = review.authorName?.trim() || 'Cliente';

  return (
    <article className="border-border rounded-xl border p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {review.authorPhotoUrl ? (
            <img src={review.authorPhotoUrl} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
              {getInitials(authorName)}
            </span>
          )}
          <div className="min-w-0">
            <h3 className="text-card-foreground truncate text-sm font-semibold">{authorName}</h3>
            <p className="text-muted-foreground text-xs">
              {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true, locale: ptBR })}
            </p>
          </div>
        </div>
        <RatingStars rating={review.rating} />
      </div>

      {review.comment ? (
        <p className="text-card-foreground mt-4 text-sm leading-6">{review.comment}</p>
      ) : (
        <p className="text-muted-foreground mt-4 text-sm italic">Avaliação sem comentário.</p>
      )}

      <div className="text-muted-foreground mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        {review.professionalName ? <span>Atendido por {review.professionalName}</span> : null}
        <span>{review.source === 'google' ? 'Avaliação do Google' : 'Avaliação verificada'}</span>
        {review.reviewUrl ? (
          <a
            href={review.reviewUrl}
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1 font-medium hover:underline"
          >
            Ver original <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

export function CompanyReviews({ companyId, enabled, fallbackRating, fallbackTotal = 0 }: CompanyReviewsProps) {
  const reviewsQuery = useCompanyReviews(companyId, enabled);
  const firstPage = reviewsQuery.data?.pages[0];
  const reviews = reviewsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const summary = firstPage?.summary;
  const average = summary?.average ?? fallbackRating ?? null;
  const total = summary?.total ?? fallbackTotal;

  if (reviewsQuery.isLoading) {
    return (
      <section className="border-border bg-card space-y-4 rounded-xl border p-5" aria-label="Carregando avaliações">
        <Skeleton className="h-8 w-44" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-36 w-full" />
      </section>
    );
  }

  if (reviewsQuery.isError) {
    return (
      <section className="border-border bg-card rounded-xl border p-5 text-center">
        <RefreshCw className="text-muted-foreground mx-auto h-7 w-7" />
        <h2 className="text-card-foreground mt-3 text-lg font-bold">Não foi possível carregar os detalhes</h2>
        <p className="text-muted-foreground mt-1 text-sm">Tente novamente para ver os comentários.</p>
        <Button variant="outline" className="mt-4" onClick={() => void reviewsQuery.refetch()}>
          Tentar novamente
        </Button>
      </section>
    );
  }

  return (
    <section className="border-border bg-card rounded-xl border p-5 sm:p-6">
      <div className="flex items-center gap-2">
        <MessageSquareText className="text-primary h-5 w-5" />
        <h2 className="text-card-foreground text-xl font-bold">Avaliações</h2>
      </div>

      <div className="mt-5 grid gap-6 sm:grid-cols-[140px_1fr] sm:items-center">
        <div className="text-center sm:border-r">
          <p className="text-card-foreground text-4xl font-bold">{average?.toFixed(1).replace('.', ',') ?? '—'}</p>
          <div className="mt-2 flex justify-center">
            <RatingStars rating={Math.round(average ?? 0)} size="lg" />
          </div>
          <p className="text-muted-foreground mt-2 text-sm">
            {total} {total === 1 ? 'avaliação' : 'avaliações'}
          </p>
        </div>

        <div className="space-y-2" aria-label="Distribuição das avaliações por estrelas">
          {[5, 4, 3, 2, 1].map((rating) => {
            const count = summary?.distribution[rating as 1 | 2 | 3 | 4 | 5] ?? 0;
            const percentage = total > 0 ? (count / total) * 100 : 0;

            return (
              <div key={rating} className="grid grid-cols-[16px_1fr_30px] items-center gap-2 text-xs">
                <span className="text-muted-foreground">{rating}</span>
                <div className="bg-muted h-2 overflow-hidden rounded-full">
                  <div className="bg-warning h-full rounded-full" style={{ width: `${percentage}%` }} />
                </div>
                <span className="text-muted-foreground text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {reviews.length > 0 ? (
        <div className="mt-6 space-y-3 border-t pt-6">
          {reviews.map((review) => (
            <ReviewCard key={`${review.source}-${review.id}`} review={review} />
          ))}
          {reviewsQuery.hasNextPage ? (
            <Button
              variant="outline"
              className="mt-2 w-full"
              disabled={reviewsQuery.isFetchingNextPage}
              onClick={() => void reviewsQuery.fetchNextPage()}
            >
              {reviewsQuery.isFetchingNextPage ? 'Carregando...' : 'Ver mais avaliações'}
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="mt-6 border-t pt-6 text-center">
          <p className="text-card-foreground font-medium">Ainda não há avaliações publicadas</p>
          <p className="text-muted-foreground mt-1 text-sm">As primeiras experiências aparecerão aqui.</p>
        </div>
      )}
    </section>
  );
}
